// @tennis-matches/services/winterdoppel/dateOverrides

import { getSupabaseClient } from "../supabase/index.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

const SELECT =
  "id,tenant_id,created_by,series_id,session_date,kind,notes,created_at,updated_at";

/**
 * @param {string} seriesId
 * @returns {Promise<Array<{ id: string, session_date: string, kind: 'force_on'|'force_off' }>>}
 */
export async function loadWinterDoppelDateOverrides(seriesId) {
  const sid = __asStr(seriesId);
  if (!sid) return [];
  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_date_overrides")
    .select(SELECT)
    .eq("series_id", sid)
    .order("session_date", { ascending: true });
  if (error) throw error;
  return (data || []).map((row) => ({
    ...row,
    session_date: String(row.session_date || "").slice(0, 10),
  }));
}

/**
 * @param {{ seriesId: string, sessionDate: string, kind: 'force_on'|'force_off', tenantId?: string|null, notes?: string|null }} input
 */
export async function upsertWinterDoppelDateOverride(input) {
  const seriesId = __asStr(input?.seriesId);
  const sessionDate = String(input?.sessionDate || "").slice(0, 10);
  const kind = __asStr(input?.kind);
  if (!seriesId || !/^\d{4}-\d{2}-\d{2}$/.test(sessionDate)) {
    throw new Error("winterdoppel.dateOverride.invalid");
  }
  if (kind !== "force_on" && kind !== "force_off") {
    throw new Error("winterdoppel.dateOverride.invalidKind");
  }
  const supabase = await getSupabaseClient();
  const row = {
    series_id: seriesId,
    session_date: sessionDate,
    kind,
    notes: input?.notes ? __asStr(input.notes) : null,
  };
  if (input?.tenantId) row.tenant_id = input.tenantId;

  const { data, error } = await supabase
    .from("winterdoppel_date_overrides")
    .upsert(row, { onConflict: "series_id,session_date" })
    .select(SELECT)
    .single();
  if (error) throw error;
  return {
    ...data,
    session_date: String(data.session_date || "").slice(0, 10),
  };
}

export async function deleteWinterDoppelDateOverride(seriesId, sessionDate) {
  const sid = __asStr(seriesId);
  const d = String(sessionDate || "").slice(0, 10);
  if (!sid || !d) return;
  const supabase = await getSupabaseClient();
  const { error } = await supabase
    .from("winterdoppel_date_overrides")
    .delete()
    .eq("series_id", sid)
    .eq("session_date", d);
  if (error) throw error;
}

/**
 * Wendet Overrides auf eine Terminliste an.
 * @param {string[]} dates
 * @param {Array<{ session_date: string, kind: string }>} overrides
 * @param {string[]} [weeklyCandidates] alle Serien-Wochentage (für force_on)
 */
export function applyWinterDoppelDateOverrides(
  dates = [],
  overrides = [],
  weeklyCandidates = null,
) {
  const forceOn = new Set();
  const forceOff = new Set();
  for (const o of overrides || []) {
    const d = String(o?.session_date || "").slice(0, 10);
    if (!d) continue;
    if (o.kind === "force_on") forceOn.add(d);
    if (o.kind === "force_off") forceOff.add(d);
  }
  const out = new Set(
    (dates || [])
      .map((d) => String(d || "").slice(0, 10))
      .filter((d) => d && !forceOff.has(d)),
  );
  const pool = Array.isArray(weeklyCandidates)
    ? weeklyCandidates
    : [...forceOn];
  for (const d of forceOn) {
    if (pool.some((p) => String(p).slice(0, 10) === d)) out.add(d);
  }
  return [...out].sort();
}

/**
 * Nächster Override-Zustand beim Klick auf ein Datum.
 * @returns {'force_on'|'force_off'|null} null = Override löschen
 */
export function nextWinterDoppelDateOverrideKind({
  isNaturalHoliday,
  overrideKind = null,
} = {}) {
  const cur = __asStr(overrideKind) || null;
  if (isNaturalHoliday) {
    if (cur === "force_on") return null;
    return "force_on";
  }
  if (cur === "force_off") return null;
  return "force_off";
}

/**
 * Toggle Override für ein Datum + Sessions neu anlegen/aufräumen.
 * @returns {Promise<{ kind: 'force_on'|'force_off'|null, sessionDate: string }>}
 */
export async function toggleWinterDoppelDateOverride({
  seriesId,
  sessionDate,
  tenantId = null,
} = {}) {
  const { getWinterDoppelSeries } = await import("./series.js");
  const { assertWinterDoppelPlanEditable } = await import("./planLock.js");
  const { ensureWinterDoppelSessions, refreshWinterDoppelDraftPlan } =
    await import("./sessions.js");
  const { isWinterDoppelNaturalHolidayDate } = await import("./groups.js");
  const series = await getWinterDoppelSeries(seriesId);
  if (!series) throw new Error("winterdoppel.series.notFound");
  assertWinterDoppelPlanEditable(series);

  const date = String(sessionDate || "").slice(0, 10);
  const isNaturalHoliday = await isWinterDoppelNaturalHolidayDate(series, date);
  const existing = await loadWinterDoppelDateOverrides(seriesId);
  const current = existing.find((o) => o.session_date === date);
  const next = nextWinterDoppelDateOverrideKind({
    isNaturalHoliday,
    overrideKind: current?.kind || null,
  });

  if (next == null) {
    await deleteWinterDoppelDateOverride(seriesId, date);
  } else {
    await upsertWinterDoppelDateOverride({
      seriesId,
      sessionDate: date,
      kind: next,
      tenantId: tenantId || series.tenant_id,
    });
  }

  await ensureWinterDoppelSessions(seriesId);
  try {
    await refreshWinterDoppelDraftPlan(seriesId);
  } catch (_) {
    /* Grobplanung optional wenn noch keine Spieler */
  }
  return { kind: next, sessionDate: date };
}
